import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { deepMerge } from '../core/deepMerge';
import { bindLabel, createPointLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import type { PointCoordsInput } from '../point/point.model';
import type { ElementTheme, PointStyle } from '../theme/theme.model';
import type { GliderArgs, GliderResult } from './glider.model';

/**
 * Resolve a theme color name to a CSS color string.
 */
function resolveColor(themeService: JsxgraphThemeService, color: ThemeColorName | undefined): string | undefined {
  if (!color) {
    return undefined;
  }
  return themeService.getColor(color);
}

/**
 * Normalize point coordinates to JSXGraph format.
 */
function normalizeCoords(coords: PointCoordsInput): [number | (() => number), number | (() => number)] {
  return coords;
}

/**
 * Resolve the slide object from element or name.
 */
function resolveSlideObject(ctx: ElementCreatorContext, slideObject: GeometryElement | string): GeometryElement {
  const { board } = ctx;

  if (typeof slideObject === 'string') {
    const found = board.elementsByName[slideObject];
    if (!found) {
      throw new Error(`Element "${slideObject}" not found on board`);
    }
    return found as GeometryElement;
  }

  return slideObject;
}

/**
 * Build JSXGraph glider attributes from style configuration.
 * Gliders use the same visual attributes as points.
 * Maps our simplified color model to JSXGraph's fillColor/strokeColor.
 */
function buildGliderAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: Required<Omit<PointStyle, 'fixed' | 'strokeColor' | 'label' | 'snapping' | 'showGrabHandle'>>,
  styleOverrides?: PointStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);

  // Our 'color' is the fill color (main visual color)
  const fillColor = resolveColor(themeService, style.color);
  // strokeColor is optional - if not set, use transparent (no stroke)
  const strokeColor = styleOverrides?.strokeColor
    ? resolveColor(themeService, styleOverrides.strokeColor)
    : 'transparent';

  return {
    size: style.size,
    face: style.face,
    strokeColor,
    fillColor,
    fillOpacity: style.fillOpacity,
    strokeWidth: styleOverrides?.strokeColor ? style.strokeWidth ?? 1 : 0,
    fixed: styleOverrides?.fixed ?? false,
    visible: style.visible,
    showInfobox: false,
    withLabel: false,
  };
}

/**
 * Create a glider element (point constrained to slide along another element).
 * Label is now configured via style.label.
 *
 * A glider is a point that is constrained to move along a geometric object
 * such as a circle, line, curve, or other supported JSXGraph elements.
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Glider creation arguments
 * @returns Glider result with element and optional label
 *
 * @example
 * ```typescript
 * // Create a glider on a circle
 * const glider = createGlider(ctx, themeService, theme, {
 *   coords: [3, 0],
 *   slideObject: circle,
 *   name: 'P',
 *   style: { label: true },
 * });
 * ```
 */
export function createGlider(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: GliderArgs,
): GliderResult {
  const { board } = ctx;
  const { coords, slideObject, name = '', style } = args;

  // Resolve the slide object
  const resolvedSlideObject = resolveSlideObject(ctx, slideObject);

  // Build glider attributes (uses point style)
  const attributes = buildGliderAttributes(themeService, theme.point, style);

  // Normalize coordinates
  const normalizedCoords = normalizeCoords(coords);

  // Create the glider
  // JSXGraph glider takes [x, y, slideObject] as parents
  const glider = board.create(
    'glider',
    [normalizedCoords[0], normalizedCoords[1], resolvedSlideObject],
    attributes,
  ) as GeometryElement;

  // Wire up drag callback if provided (for param store sync)
  if (args.onDrag) {
    const typedGlider = glider as unknown as {
      X: () => number;
      Y: () => number;
      on: (event: string, handler: () => void) => void;
    };
    typedGlider.on('drag', () => {
      args.onDrag!(typedGlider.X(), typedGlider.Y());
    });
  }

  const result: GliderResult = { glider };

  // Create label if configured - now from style.label
  const labelConfig = resolveLabelConfig(style?.label, {
    color: style?.color ?? 'neutral',
  });

  if (labelConfig && name) {
    result.label = bindLabel({
      ctx,
      themeService,
      element: glider,
      config: labelConfig,
      contextProvider: createPointLabelContext(name, glider),
    });
  }

  return result;
}
